import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import { ApolloProvider, ApolloClient, InMemoryCache, HttpLink, ApolloLink, Observable } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
// import { AuthLink } from './Helpers/authLink.js';
import { setContext } from '@apollo/client/link/context';
import axios from "axios";

import store from "./store.js";
import GeolocationProvider from './Components/Pages/Stakeholder/Geolocation/hooks/geolocationContext';

import DVMSRouter from './DVMSRouter.jsx';
import ErrorBoundary from './Components/ErrorBoundary';
import { ModalContainer } from 'react-router-modal';
import '../../../node_modules/react-router-modal/css/react-router-modal.css';
import './i18n';
import ReactTooltip from 'react-tooltip';
// import { destroyCredentials, setUserSession } from './Actions/userSessionActions.js';
// import { TokenRefreshLink } from 'apollo-link-token-refresh';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "react-checkbox-tree/lib/react-checkbox-tree.css";
// import jwt_decode from 'jwt-decode';
import { logOut } from './Helpers/checkCredentials.js';
import { cache } from './GraphQL/cache.js';

import * as Sentry from '@sentry/react';

let APP_URL = window.location.hostname;
if (APP_URL.includes('www.'))
    APP_URL = APP_URL.split('www.')[1];

const API_URL = process.env.MIX_API_URL || `https://api.${APP_URL}/graphql`;

const urlsToValidate = ['/ajax/vehicle', '/ajax/booking', '/ajax/release', '/ajax/return', 'ajax/retirevehicle'];

// Legacy API axios request interceptor
axios.interceptors.request.use(async (config) => {
  const shouldValidateCurrentURL = urlsToValidate.some((url) => config.url.includes(url));
  if (!shouldValidateCurrentURL) return config;

  const isAccessTokenExpired = checkAccessTokenExpired();
  if (!isAccessTokenExpired) return config;

  try {
    const tokenResponse = await fetchAccessToken();

    if (!tokenResponse?.data) {
      console.log('Could not fetch the new token');
      // Should we call logOut() here???
      return config;
    }

    localStorage.setItem('access_token', tokenResponse?.data?.refreshAccessToken?.accessToken);
    localStorage.setItem('refresh_token', tokenResponse?.data?.refreshAccessToken?.refreshToken);
    localStorage.setItem('sub', tokenResponse?.data?.refreshAccessToken?.userInfo.sub);

    // config.headers.Authorization = `Bearer ${tokenResponse?.data?.refreshAccessToken?.accessToken}`;
    console.log('Axios interceptor refreshed AccessToken');

    return config;
  } catch (error) {
    console.log('Axios interceptor fetchAccessToken error: ', error);
    logOut();
  }
});

export const checkAccessTokenExpired = () => {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken || typeof accessToken !== 'string') return true;

  const decodedJwt = JSON.parse(atob(accessToken.split(".")[1]));
  if (!decodedJwt?.exp) return true;

  const expiration = decodedJwt.exp * 1000;
  const isTokenExpired = expiration < Date.now();
  return isTokenExpired;
};

const fetchAccessToken = async () => {
  const sub = localStorage.getItem('sub');
  const refreshToken = localStorage.getItem('refresh_token');
  if (sub && refreshToken) {
    const resp = await fetch(API_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query: `query RefreshToken($sub: ID!, $refreshToken: String!) {
          refreshAccessToken(input: { sub: $sub, refreshToken: $refreshToken }) {
              accessToken
              refreshToken
              expiresIn
              userInfo {
                  sub
                  issuedBy
                  name
              }
          }
        }`,
        variables: {
          sub: sub,
          refreshToken: refreshToken,
        },
      })
    });
    return resp.json().then((res) => {
      return res;
    });
  } else {
    // logOut();
  }
}

const httpLink = new HttpLink({
    uri: API_URL,
    credentials: 'same-origin'
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem('access_token');
  if (!token) {
    fetchAccessToken()
    .then((new_token) => {
      // console.log(new_token);
      if (new_token && new_token.data) {
        localStorage.setItem('access_token', new_token.data.refreshAccessToken.accessToken);
        localStorage.setItem('refresh_token', new_token.data.refreshAccessToken.refreshToken);
        localStorage.setItem('sub', new_token.data.refreshAccessToken.userInfo.sub);
        return {
          headers: {
            ...headers,
            authorization: `Bearer ${new_token}`,
          }
        }
      } else {
        // console.log('couldnt fetch the new token');
      }
    })
    .catch((err) => {
      // console.log('auth link error: ', err);
      logOut();
    })
  } else {
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      }
    }
  }
  // return the headers to the context so httpLink can read them

});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {

    const isLogin = operation.operationName === 'LoginGraphQL';
    const isUnauthenticated = graphQLErrors[0].extensions.code === 'UNAUTHENTICATED';
    if (isLogin && isUnauthenticated) return;

    console.error('error: ', graphQLErrors, networkError);

    for (let err of graphQLErrors) {
      switch (err.extensions.code) {
        case 'UNAUTHENTICATED':
          console.log('unauthenticated');
          localStorage.removeItem('access_token');
          fetchAccessToken()
          .then((token) => {
            localStorage.setItem('access_token', token.data.refreshAccessToken.accessToken);
            localStorage.setItem('refresh_token', token.data.refreshAccessToken.refreshToken);
            localStorage.setItem('sub', token.data.refreshAccessToken.userInfo.sub);
            showStatusMsg('Refreshing credentials, please wait...', 'info');
            setTimeout(()=>{
              window.location.reload();
            }, 1000)
            // return forward(operation);
          })
          .catch((err) => {
            // console.log('error: ', err);
            logOut();
          })
          // logOut().then((res) => {
          //   console.log('log out res: ', res);
            // window.location.reload();
          // });
          return forward(operation);
        case 'BAD_USER_INPUT':
          const errorMessage = err.extensions?.response?.message;
          if (errorMessage?.length) showStatusMsg(errorMessage.join(', '), 'error');

          if (err.message) showStatusMsg(err.message, 'error');

          console.log('bad user input');
          console.log('errorMessage: ', errorMessage);
          console.log('err.message: ', err.message);
          break;
        case 'INTERNAL_SERVER_ERROR':
          const internalErrorMessage = err.extensions?.exception?.message;
          if (internalErrorMessage) showStatusMsg(internalErrorMessage, 'error');

          console.log('internal server error');
          console.log('error message: ', internalErrorMessage);
          break;
      }
    }
  } if (networkError) {
    console.log('network error: ', networkError);
    // logOut();
  }
});

export const apolloClient =  new ApolloClient({
    link: errorLink.concat(authLink).concat(httpLink),
    cache: cache,
});

// const tokenRefreshLink = new TokenRefreshLink({
//   // accessTokenField: 'data.refreshAccessToken.accessToken',
//   isTokenValidOrUndefined: () => {
//     const accessToken = localStorage.getItem('access_token');
//     const refreshToken = localStorage.getItem('refresh_token');
//     if (!accessToken) {
//       if (!refreshToken)
//         store.dispatch(destroyCredentials());
//       return false;
//     }
//     try {
//       const { exp } = jwt_decode(accessToken, {header: true});
//       const { exp: exp2 } = jwt_decode(refreshToken, {header: true});
//         if (Date.now() >= exp * 1000) {
//           if (Date.now() >= exp2 * 1000) {
//             logOut();
//           } else {
//             return false;
//           }
//         } else return true;
//     } catch (err) {
//         return false;
//     }
// },
//   fetchAccessToken,
//   handleResponse: (operation, accessTokenField) => (response) => {
//     if (response) {
//       return response.json()
//         .then(({data}) => {
//           if (data && data.refreshAccessToken) {
//             localStorage.setItem('access_token', data.refreshAccessToken.accessToken);
//             localStorage.setItem('refresh_token', data.refreshAccessToken.refreshToken);
//             return data.refreshAccessToken.accessToken;
//           } else {
//             logOut();
//           }
//         })
//         .catch((err) => {
//           logOut();
//         })
//     } else {
//       if (!operation.operationName.includes('Login')) {
//         logOut();
//       }
//     }
//   },
//   handleError: (error) => {
//     if (! error.stack.includes('_this.extractToken')) { //bug with the apollo auth client, easy workaround
//       const refreshToken = localStorage.getItem('refresh_token');
//       if (refreshToken) {
//         const { exp } = jwt_decode(refreshToken);
//         if (!exp || Date.now() >= exp * 1000) {
//           logOut();
//         }
//       } else {
//         logOut();
//       }
//     }
//   }
// });

// const link = ApolloLink.from([errorLink, authLink, httpLink]);

const environment = process.env.NODE_ENV || window.location.hostname.includes('fxp.tech') ? 'production' : 'testing';

Sentry.init({
  dsn: 'https://a4d19bb65b2645e9b226d67bc01fd4aa@o4505369795362816.ingest.sentry.io/4505369797132288',
  environment,
  integrations: [
    new Sentry.BrowserTracing({
      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ['localhost', 'https://fxp.dev', 'https://testing.thedvms.com', 'https://fxp.tech'],
    }),
    new Sentry.Replay(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const Index = (props) => {
    return (
        <Provider store={store}>
            <ApolloProvider client={apolloClient}>
                <GeolocationProvider>
                    <Router>
                        <ErrorBoundary>
                            <div>
                                <DVMSRouter 	
                                    sessionActive={props.session_active}	
                                    sessionType={props.session_type} 
                                    username={props.username} 
                                    fullname={props.full_name} 
                                    email={props.email} 
                                    userId={props.user_id} 
                                    tempPassword={props.temp_password} 
                                    sessionOemName={props.current_oem_name} 
                                    sessionOemId={props.current_oem_id} 
                                    logo={props.logo}
                                    navLogo={props.nav_logo}
                                    loginLogo={props.login_logo}
                                    serverEmailName={props.server_email_name}
                                />
                                <ModalContainer />
                                <ToastContainer position='bottom-left'/>
                            </div>
                            <ReactTooltip effect="solid" />
                        </ErrorBoundary>
                    </Router>
                </GeolocationProvider>
            </ApolloProvider>
        </Provider>
    )
}

if (document.getElementById('root')) {
    const el = document.getElementById('root')
    const props = Object.assign({}, el.dataset)
    ReactDOM.render(<Index {...props} />, el);
}